<template>
    <div class="customer__form modal modal--default mandate-email-modal">
        <div class="modal__panel">
            <div class="modal__logo-wrapper">
                <div class="modal__logo"></div>
            </div>
            <div class="modal__main-icon modal__main-icon--email"></div>
            <div class="modal__close" @click="closeModal()"></div>

            <div class="form">
                <div class="navigators">
                    <div class="navigator navigator--active">
                        <div class="navigator__bullet"></div>
                        <div class="navigator__title">{{ $t(`mandateEmailModal.${"title"}`) }}</div>
                    </div>
                </div>

                <div class="add__form">
                    <transition name="fade" mode="out-in">
                        <Loading v-if="isLoading" />
                        <div v-else class="add__content">
                            <div class="mandate-email-content">
                                <div class="form__field team-select-input">
                                    <MultiSelectDropdown :items="companies"
                                        :placeholder="$t(`addMandateModal.${'companies'}`)" label="name"
                                        :selected="emailData.companies" class="team-select"
                                        :class="{ 'input--error': companyHasError }"
                                        @input="onCompaniesSelected($event)" />
                                    <div class="form__error" v-if="companyHasError">
                                        {{ $t(`mandateEmailModal.${"companyErrorMsg"}`) }}
                                    </div>
                                </div>

                                <div class="company-policies">
                                    <div v-for="company in emailData.companies" :key="company.id"
                                        class="company-policy-row">
                                        <div class="company-name-wrapper">
                                            <div class="company-name">

                                                <button @click="removeCompany(company.id)" type="button"
                                                    class="button remove-company-btn" title="Remove this company">
                                                    <div class="button__icon"></div>
                                                </button>
                                                <p>{{ company.name }}</p>

                                                <div class="mandat">
                                                    <label class="mandat__title" :for="'company-' + company.id">{{
                                                        $t(`addMandateModal.${"singleMandate"}`) }}</label>
                                                    <div class="checkbox">
                                                        <input type="checkbox" :id="'mandatCheckbox-' + company.id"
                                                            class="checkbox__checkbox"
                                                            v-model="company.single_mandate" />
                                                        <label :for="'mandatCheckbox-' + company.id"
                                                            class="checkbox__label"></label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="policy-numbers">
                                            <div class="policy-numbers-container">
                                                <div v-for="(policyNum, index) in company.policyNumbers" :key="index"
                                                    class="policy-number-row">
                                                    <div class="form__field">
                                                        <input v-model="company.policyNumbers[index]" type="text"
                                                            class="input input--default"
                                                            :class="{ 'input--error': company.single_mandate && !policyNum.trim() }"
                                                            :placeholder="$t(`addMandateModal.${'policyNumber'}`)" @keydown.enter.prevent />
                                                        <div class="form__error"
                                                            v-if="company.single_mandate && !policyNum.trim()">
                                                            {{ $t(`mandateEmailModal.${"policyNumberErrorMsg"}`) }}
                                                        </div>
                                                    </div>

                                                    <button v-if="company.policyNumbers.length > 1"
                                                        @click="removePolicyNumber(company.id, index)" type="button"
                                                        class="button delete-policy-btn policy-btn">
                                                        <div class="button__icon"></div>
                                                    </button>
                                                </div>
                                            </div>
                                            <button @click="addPolicyNumber(company.id)"
                                                class="button button--default policy-btn add-policy-btn">
                                                <div class="button__icon"></div>
                                            </button>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
            <div class="modal__buttons">
                <div v-if="isRequesting || isLoading"
                    class="modal__button modal__button--submit button button--default button--loading"></div>
                <button v-else class="modal__button modal__button--submit button button--default" @click="sendEmail()"
                    :disabled="isRequesting || isLoading">
                    <span>{{ $t(`mandateEmailModal.${"send"}`) }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

import Loading from "@/partials/Loading.vue";
import MultiSelectDropdown from "@/partials/MultiSelectDropdown.vue";

import { useI18n } from 'vue-i18n';   

// eslint-disable-next-line no-unused-vars
const { t } = useI18n();

const store = useStore();
const route = useRoute();

const emailData = ref({
    companies: [],
});

const isLoading = ref(false);
const isRequesting = ref(false);
const companyHasError = ref(false);
const teamId = ref(null);
const selectedCompanies = ref([]);

const mandateEmailModal = computed(() =>
    store.getters.mandateEmailModal
);

const customerDetails = computed(() =>
    store.getters.customerDetails
);

const mandateCompaniesForms = computed(() =>
    store.getters.mandateCompaniesForms
);


const companies = computed(() => {
    if (!mandateCompaniesForms.value) return [];

    return mandateCompaniesForms.value.companies || [];
});

onMounted(async () => {
    isLoading.value = true;

    const customer_id = route.params.id;
    const mandate_id = mandateEmailModal.value.mandateId;

    if (customerDetails.value?.team) {
        teamId.value = Number(customerDetails.value.team.id);

        await store.dispatch(
            "getMandateCompaniesForms",
            teamId.value
        );
    }

    if (customer_id && mandate_id) {

        const draftsResponse = await store.dispatch(
            "getMandateCompanyDeliveryDrafts",
            {
                customer_id,
                mandate_id
            }
        );

        const drafts = Array.isArray(draftsResponse)
            ? draftsResponse
            : draftsResponse.data || [];


        const groupedCompanies = {};

        drafts.forEach(item => { 

            const company = companies.value.find(
                c => c.id === item.company_id
            );


            if (!groupedCompanies[item.company_id]) {

                groupedCompanies[item.company_id] = {
                    id: item.company_id,
                    name: company?.name || "",
                    policyNumbers: [],
                    single_mandate: item.single_mandate || false
                };
            }

            if (item.policy_number) {
                groupedCompanies[item.company_id].policyNumbers.push(
                    item.policy_number
                );
            }
        });

        emailData.value.companies = Object.values(groupedCompanies).map(company => ({
            ...company,
            policyNumbers: company.policyNumbers.length
                ? company.policyNumbers
                : [""],
            single_mandate: company.single_mandate || false
        }));

        selectedCompanies.value = emailData.value.companies;
    }

    isLoading.value = false;
});


const onCompaniesSelected = (selectedCompanies) => {
    const existingCompanyIds = new Set(
        emailData.value.companies.map(c => c.id)
    );

    const selectedCompanyIds = new Set(
        selectedCompanies.map(c => c.id)
    );


    // Remove companies that are no longer selected
    emailData.value.companies =
        emailData.value.companies.filter(c =>
            selectedCompanyIds.has(c.id)
        );


    // Add new companies
    selectedCompanies.forEach(company => {

        if (!existingCompanyIds.has(company.id)) {

            emailData.value.companies.push({
                id: company.id,
                name: company.name,
                policyNumbers: [""],
                single_mandate: false
            });

        }

    });
};


const addPolicyNumber = (companyId) => {

    const company = emailData.value.companies.find(
        c => c.id === companyId
    );

    if (company) {
        company.policyNumbers.push("");
    }

};


const removePolicyNumber = (companyId, index) => {

    const company = emailData.value.companies.find(
        c => c.id === companyId
    );

    if (company) {
        company.policyNumbers.splice(index, 1);
    }

};


const removeCompany = (companyId) => {

    emailData.value.companies =
        emailData.value.companies.filter(
            c => c.id !== companyId
        );

};


const sendEmail = async () => {

    try {

        if (
            !emailData.value.companies ||
            emailData.value.companies.length === 0
        ) {

            companyHasError.value = true;

            store.commit("setToast", {
                display: true,
                type: "error",
                message: t("addModelModal.companyErrorMsg"),
            });

            return;
        }

        // Validate policy numbers based on single_mandate state
        const isValid = emailData.value.companies.every(company => {
            if (company.single_mandate) {
                // If single_mandate is checked, ALL policy numbers must be filled
                return company.policyNumbers.every(num => num.trim());
            } else {
                // If single_mandate is not checked, policy numbers are optional
                return true;
            }
        });

        if (!isValid) {
            store.commit("setToast", {
                display: true,
                type: "error",
                message:  t("mandateEmailModal.fillPolicyNr"),
            });
            return;
        }

        companyHasError.value = false;

        isRequesting.value = true;


        const customer_id = route.params.id;

        const mandate_id =
            mandateEmailModal.value.mandateId;


        const deliveries =
            emailData.value.companies.map(company => {

                const policyNumbers =
                    company.policyNumbers.filter(
                        num => num.trim()
                    );


                return {
                    company_id: company.id,
                    policy_numbers:
                        policyNumbers.length > 0
                            ? policyNumbers
                            : null,
                    single_mandate: company.single_mandate
                };

            });



        await store.dispatch(
            "sendMandateEmail",
            {
                customer_id,
                mandate_id,
                deliveries,
            }
        );


        await store.dispatch(
            "fetchMandates",
            customer_id
        );


        store.commit("setToast", {
            display: true,
            type: "success",
            message:  t("mandateEmailModal.emailSent"),
        });


        closeModal();


    } catch (error) {

        console.error(
            "Error sending email:",
            error
        );


        store.commit("setToast", {
            display: true,
            type: "error",
            message:
                error.response?.data?.message ||
                t("mandateEmailModal.errorSending"),
        });


    } finally {

        isRequesting.value = false;

    }

};


const closeModal = () => {

    store.commit(
        "setMandateEmailModal",
        {
            display: false,
            mandateId: null,
        }
    );

    resetForm();

};


const resetForm = () => {

    emailData.value = {
        companies: [],
    };

};

// const onCompanyCheckboxChange = () => {
//     // Error handling is done in sendEmail validation
//     companyHasError.value = false;
// };
</script>

<style scoped lang="scss">
.modal__panel {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.form {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 100%;
}

.customer__form .form .add__form {
    flex: 1;
    width: 100%;
    overflow: hidden;

}

.add__content {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0 !important;
}

.mandate-email-content {
    width: 100%;
    margin: 0 auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.team-select-input {
    width: 50%;
    margin: 0 auto;
    margin-bottom: 40px;
    flex-shrink: 0;
}

.company-policies {
    display: flex;
    flex-direction: column;
    gap: 20px;
    overflow-y: auto;
    flex: 1;
}

.company-policy-row {
    display: flex;
    align-items: flex-start;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--bg-2);

    &:last-child {
        border-bottom: none;
        padding-bottom: 0px;
    }

    .company-name-wrapper {
        width: 50%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-top: 8px;
    }

    .company-name {
        display: flex;
        align-items: center;
        gap: 10px;
        flex: 1;

        p {
            color: var(--text-2);
            font-size: 16px;
            width: 50%;
        }

        button {
            transform: scale(0.8);
        }
    }

    .policy-numbers {
        display: flex;
        align-items: flex-start;
        justify-content: center;
        width: 50%;
    }

    .policy-numbers-container {
        width: 90%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 10px;

        .policy-number-row {
            display: flex;
            gap: 5px;
            width: 100%;

            .form__field {
                width: 90%;
                margin-bottom: 10px;

                input {
                    width: 100%;
                }
            }
        }
    }

    .policy-btn {
        transform: scale(0.7);
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;

        .button__icon {
            margin: auto;
            width: 20px;
            height: 20px;
            background-image: url("@/assets/icons/default/add_icon.svg");
            background-repeat: no-repeat;
            background-position: center;
        }
    }

    .delete-policy-btn {
        background-color: #f44336;

        &:hover {
            background-color: #da190b;
            box-shadow: 0px -7px 18px rgba(0, 0, 0, 0.2) inset;
        }

        .button__icon {
            transform: rotate(45deg) !important;
        }
    }

    .remove-company-btn {
        background-color: #f44336;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        &:hover {
            background-color: #da190b;
            box-shadow: 0px -7px 18px rgba(0, 0, 0, 0.2) inset;
        }

        .button__icon {
            width: 16px;
            height: 16px;
            background-image: url("../assets/icons/default/add_icon.svg");
            background-repeat: no-repeat;
            background-position: center;
            transform: rotate(45deg);
        }
    }
}

.mandat {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
}

.mandat__title {
    cursor: pointer;
    color: var(--text-2);
    font-family: var(--muli-text-semi-bold);
    margin-left: 15px;
}
</style>
